import API_CONFIG from '../config/api.config';
import apiService from './api.service';

/**
 * Admin Service
 * Admin işlemleri için API çağrıları
 */

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  message: string;
  data: any; // Backend'den dönen admin bilgileri
  tokens?: {
    accessToken: string;
  };
  status: boolean;
  error: any;
}

class AdminService {
  async login(credentials: LoginCredentials): Promise<AdminLoginResponse> {
    return apiService.post(API_CONFIG.ENDPOINTS.ADMIN_LOGIN, credentials);
  }

  async createAdmin(data: { email: string; password: string; fullname: string }) {
    return apiService.post(API_CONFIG.ENDPOINTS.ADMIN_CREATE, data);
  }

  // User Management
  async getAllUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    return apiService.get(API_CONFIG.ENDPOINTS.GET_ALL_USERS, { params });
  }

  async getUserDetails(userId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_USER_DETAILS, { userId });
    return apiService.get(url);
  }

  async sendWarning(data: { userId: string; warningTitle: string; warningMessage: string }) {
    return apiService.post(API_CONFIG.ENDPOINTS.SEND_WARNING, data);
  }

  async banUser(userId: string, isBanned: boolean) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.BAN_USER, { userId });
    return apiService.patch(url, { isBanned });
  }

  async deleteUser(userId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_USER, { userId });
    return apiService.delete(url);
  }

  async updateUser(userId: string, data: any) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.UPDATE_USER, { userId });
    return apiService.patch(url, data);
  }

  async givePremiumToUser(userId: string) {
    return apiService.post(API_CONFIG.ENDPOINTS.GIVE_PREMIUM_TO_USER, { userId });
  }

  async getUserPostsStats(userId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_USER_POSTS_STATS, { userId });
    return apiService.get(url);
  }

  async getUserPosts(userId: string, params: { page?: number; limit?: number; includeDeleted?: boolean }) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_USER_POSTS, { userId });
    return apiService.get(url, { params });
  }

  async deletePost(postId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_POST, { postId });
    return apiService.delete(url);
  }

  // Group Request Management
  async getAllGroupRequests(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    return apiService.get(API_CONFIG.ENDPOINTS.GET_ALL_GROUP_REQUESTS, { params });
  }

  async getGroupRequestDetail(requestId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_GROUP_REQUEST_DETAIL, { requestId });
    return apiService.get(url);
  }

  async approveGroupRequest(requestId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.APPROVE_GROUP_REQUEST, { requestId });
    return apiService.post(url);
  }

  async rejectGroupRequest(requestId: string, rejectionReason: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.REJECT_GROUP_REQUEST, { requestId });
    return apiService.post(url, { rejectionReason });
  }

  // Group Management
  async getAllGroups(params: { page?: number; limit?: number; search?: string }) {
    return apiService.get(API_CONFIG.ENDPOINTS.GET_ALL_GROUPS, { params });
  }

  async createGroup(data: {
    groupName: string;
    groupDescription: string;
    groupAvatar?: string;
    groupSchoolName: string;
    isPrivateChat: boolean;
  }) {
    return apiService.post(API_CONFIG.ENDPOINTS.CREATE_GROUP, data);
  }

  async getGroupDetail(chatId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_GROUP_DETAIL, { chatId });
    return apiService.get(url);
  }

  async updateGroup(chatId: string, data: any) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.UPDATE_GROUP, { chatId });
    return apiService.patch(url, data);
  }

  async deleteGroup(chatId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_GROUP, { chatId });
    return apiService.delete(url);
  }

  async activateGroup(chatId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.ACTIVATE_GROUP, { chatId });
    return apiService.patch(url);
  }

  async getGroupMessages(chatId: string, params: { page?: number; limit?: number; includeDeleted?: boolean }) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_GROUP_MESSAGES, { chatId });
    return apiService.get(url, { params });
  }

  async deleteGroupMessage(chatId: string, messageId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_GROUP_MESSAGE, { chatId, messageId });
    return apiService.delete(url);
  }

  // Report Management
  async getAllReports(params: {
    page?: number;
    limit?: number;
    status?: string;
    reportType?: string;
    search?: string;
  }) {
    return apiService.get(API_CONFIG.ENDPOINTS.GET_ALL_REPORTS, { params });
  }

  async getReportDetail(reportId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_REPORT_DETAIL, { reportId });
    return apiService.get(url);
  }

  async resolveReport(reportId: string, data: { action: string; note: string }) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.RESOLVE_REPORT, { reportId });
    return apiService.post(url, data);
  }

  async deleteReport(reportId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_REPORT, { reportId });
    return apiService.delete(url);
  }

  async getUserReports(userId: string, params: { page?: number; limit?: number }) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.GET_USER_REPORTS, { userId });
    return apiService.get(url, { params });
  }

  // ETC Management - Initial Config
  async getInitialConfig() {
    return apiService.get(API_CONFIG.ENDPOINTS.GET_INITIAL_CONFIG);
  }

  // ETC Management - Cities
  async addCities(cities: { plateCode: number; cityName: string }[]) {
    return apiService.post(API_CONFIG.ENDPOINTS.ADD_CITIES, { cities });
  }

  async updateCities(cities: { plateCode: number; cityName: string }[]) {
    return apiService.put(API_CONFIG.ENDPOINTS.UPDATE_CITIES, { cities });
  }

  async deleteCity(plateCode: number) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_CITY, { plateCode: plateCode.toString() });
    return apiService.delete(url);
  }

  // ETC Management - Universities
  async addUniversities(universities: { universityId: string; universityName: string; universityCity: string; universityMail: string }[]) {
    return apiService.post(API_CONFIG.ENDPOINTS.ADD_UNIVERSITIES, { universities });
  }

  async updateUniversities(universities: { universityId: string; universityName: string; universityCity: string; universityMail: string }[]) {
    return apiService.put(API_CONFIG.ENDPOINTS.UPDATE_UNIVERSITIES, { universities });
  }

  async deleteUniversity(universityId: string) {
    const url = apiService.buildUrl(API_CONFIG.ENDPOINTS.DELETE_UNIVERSITY, { universityId });
    return apiService.delete(url);
  }

  // ETC Management - App Config
  async updateAppConfig(data: {
    appVersion?: string;
    appBuildNumberIOS?: string;
    appBuildNumberAndroid?: string;
    appMaintenanceMode?: boolean;
    appMaintenanceMessage?: string;
    appMaintenanceEstimatedTime?: number;
  }) {
    return apiService.put(API_CONFIG.ENDPOINTS.UPDATE_APP_CONFIG, data);
  }

  async updateSocialMediaLinks(socialMediaLinks: {
    instagram: string;
    twitter: string;
    linkedin: string;
    youtube: string;
    tiktok: string;
  }) {
    return apiService.put(API_CONFIG.ENDPOINTS.UPDATE_SOCIAL_MEDIA_LINKS, { socialMediaLinks });
  }

  async updateStoreUrls(data: {
    storeUrlIOS: string;
    storeUrlAndroid: string;
  }) {
    return apiService.put(API_CONFIG.ENDPOINTS.UPDATE_STORE_URLS, data);
  }
}

export default new AdminService();

